import { IconBellRing } from '@developerz/ui';
import { type JSX, onMount, Show } from 'solid-js';
import { t } from '../i18n';
import { browserPushRuntime, type PushRuntime } from '../lib/push';
import { createPushToggle, PUSH_FAILURE_KEY } from '../lib/push-toggle';
import { buttonClass } from './ui/Button';

// The topbar's bell button over the Web Push state machine (`lib/push-toggle.ts`,
// #5703): subscribes THIS browser and registers the target server-side; toggling
// off unsubscribes and deletes. Hidden where push is unsupported — a dead-end
// toggle is worse than none. `runtime` is injectable so the machine is testable.
// `onChange` fires after a subscribe or an unsubscribe lands (never on the
// initial reflect); the topbar mount omits it.
//
// While `failure()` is set (`#5884`), the failure sentence is appended to the
// button's `title` and `aria-label`, so a keyboard or screen-reader user who
// activates the bell hears WHY the last click was a no-op rather than a silent
// bounce back to Off.

export function PushToggle(props: { runtime?: PushRuntime; onChange?: () => void }): JSX.Element {
  const push = createPushToggle(props.runtime ?? browserPushRuntime(), () => props.onChange?.());

  onMount(() => void push.init());

  const baseLabel = (): string => {
    switch (push.status()) {
      case 'on':
        return t('push.on');
      case 'working':
        return t('push.working');
      case 'blocked':
        return t('push.blocked');
      default:
        return t('push.enable');
    }
  };
  const failureText = (): string => {
    const reason = push.failure();
    return reason === null ? '' : t(PUSH_FAILURE_KEY[reason]);
  };
  const label = (): string => {
    const fail = failureText();
    return fail === '' ? baseLabel() : `${baseLabel()} — ${fail}`;
  };

  return (
    <Show when={push.status() !== 'loading' && push.status() !== 'unsupported'}>
      {/* Ghost icon button like its topbar neighbors (ThemeToggle) — those three
          `btn*` classes carry the whole appearance. */}
      <button
        type="button"
        class={buttonClass({ variant: 'ghost', size: 'icon' })}
        onClick={() => void (push.status() === 'on' ? push.disable() : push.enable())}
        disabled={push.status() === 'working' || push.status() === 'blocked'}
        aria-pressed={push.status() === 'on'}
        aria-label={label()}
        title={label()}
      >
        <IconBellRing size="1rem" />
      </button>
    </Show>
  );
}
